import {
  NEGATIVE_CASH_POLICIES,
  NEGATIVE_CASH_POLICY_LABELS,
  type NegativeCashPolicy,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  MoneyInput,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { useTreasurySettings } from "../../../features/treasury/definitions.js";
import { TreasuryPage, useRefreshTreasury } from "../../../features/treasury/shared.js";
import { minorOf } from "../../../features/treasury/treasury-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/ayarlar")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: TreasurySettingsPage,
});

function TreasurySettingsPage() {
  const { t } = useTranslation();
  return (
    <TreasuryPage title={t("menu.treasurySettings")} description={t("treasury.settings.description")}>
      {({ companyId }) => <SettingsForm companyId={companyId} />}
    </TreasuryPage>
  );
}

function SettingsForm({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshTreasury();
  const canManage = usePermissions().has("treasury.definition.manage");
  const settings = useTreasurySettings(companyId);
  const [policy, setPolicy] = useState<NegativeCashPolicy | null>(null);
  const [memoPosting, setMemoPosting] = useState<boolean | null>(null);
  const [tolerance, setTolerance] = useState<string | null | undefined>(undefined);
  const [reminderDays, setReminderDays] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (settings.isPending) return <SkeletonRows rows={5} />;
  if (settings.error) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;

  const current = settings.data;
  // Kullanıcı bir alana dokunmadıysa sunucudaki değer gösterilir (denetimli alanlar, geç gelen veriye dayanıklı).
  const value = {
    policy: policy ?? current.negativeCashPolicy,
    memoPosting: memoPosting ?? current.guaranteeMemoPosting,
    tolerance: tolerance === undefined ? current.posMismatchTolerance : tolerance,
    reminderDays: reminderDays ?? current.reminderDays.join(", "),
  };

  const parsedDays = value.reminderDays
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((part) => Number(part))
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 365);

  async function submit(): Promise<void> {
    if (parsedDays.length === 0) return setError(t("treasury.settings.reminderDaysInvalid"));
    setBusy(true);
    setError(null);
    try {
      await api.PUT("/treasury/settings", {
        params: { query: { companyId } },
        body: {
          negativeCashPolicy: value.policy,
          guaranteeMemoPosting: value.memoPosting,
          posMismatchTolerance: minorOf(value.tolerance).toString(),
          reminderDays: [...new Set(parsedDays)].sort((left, right) => right - left),
        },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("common.saved") });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="grid max-w-2xl gap-4 p-5">
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField
          label={t("treasury.settings.negativeCashPolicy")}
          description={t("treasury.settings.negativeCashPolicyHint")}
          required
        >
          {(control) => (
            <Select
              {...control}
              value={value.policy}
              disabled={!canManage}
              onChange={(event) => setPolicy(event.target.value as NegativeCashPolicy)}
            >
              {NEGATIVE_CASH_POLICIES.map((item) => (
                <option key={item} value={item}>
                  {NEGATIVE_CASH_POLICY_LABELS[item]}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-0.5"
            checked={value.memoPosting}
            disabled={!canManage}
            onChange={(event) => setMemoPosting(event.target.checked)}
          />
          <span className="grid gap-0.5">
            <span>{t("treasury.settings.guaranteeMemoPosting")}</span>
            <span className="text-xs text-muted-foreground">
              {t("treasury.settings.guaranteeMemoPostingHint")}
            </span>
          </span>
        </label>

        <FormField
          label={t("treasury.settings.posMismatchTolerance")}
          description={t("treasury.settings.posMismatchToleranceHint")}
        >
          {(control) => (
            <MoneyInput
              {...control}
              value={value.tolerance}
              disabled={!canManage}
              onValueChange={setTolerance}
            />
          )}
        </FormField>

        <FormField
          label={t("treasury.settings.reminderDays")}
          description={t("treasury.settings.reminderDaysHint")}
          required
        >
          {(control) => (
            <Input
              {...control}
              value={value.reminderDays}
              disabled={!canManage}
              inputMode="numeric"
              onChange={(event) => setReminderDays(event.target.value)}
            />
          )}
        </FormField>

        {canManage ? (
          <div className="flex justify-end">
            <Button type="submit" loading={busy}>
              {t("common.save")}
            </Button>
          </div>
        ) : (
          <Alert tone="info">{t("treasury.settings.readOnly")}</Alert>
        )}
      </form>
    </Card>
  );
}
